import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useUserTokens, useDeletePersonalAccessToken } from '@/hooks/useApiTokens';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { KeyIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { CreateTokenModal } from '@/components/api-tokens/CreateTokenModal';
import { useToast } from '@/hooks/useToast';
import { loadAuthGuard } from '@/lib/auth-helpers';

interface PersonalAccessToken {
  id: string;
  organizationId: string | null;
  createdBy: string;
  name: string;
  token: string;
  permissions: string[];
  allowedOrganizations: string[] | null;
  status: 'active' | 'revoked' | 'expired';
  lastUsed?: string;
  expiresAt?: string;
  createdAt: string;
}

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const Route = createFileRoute('/_authenticated/settings/api-tokens')({
  loader: loadAuthGuard(async ({ context, ensureQueryData, sessionReady }) => {
    if (!sessionReady) {
      return {};
    }

    // Preload organizations for context
    await ensureQueryData(
      context.trpc.organizations.getAll.queryOptions(undefined),
    );

    // Preload user tokens
    await ensureQueryData(
      context.trpc.apiTokens.getUserTokens.queryOptions(undefined),
    );

    return {};
  }),
  component: ApiTokensPage,
});

function ApiTokensPage() {
  const { data: tokensData, isLoading } = useUserTokens();
  const deleteTokenMutation = useDeletePersonalAccessToken();
  const toast = useToast();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const tokens: PersonalAccessToken[] = Array.isArray(tokensData) ? tokensData : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading tokens..." />
      </div>
    );
  }

  const activeTokens = tokens.filter(t => t.status === 'active');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Personal Access Tokens</h1>
          <p className="text-gray-600 mt-2">Manage your personal access tokens for API and CLI access</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Token
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Tokens</CardTitle>
          <KeyIcon className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activeTokens.length}</div>
          <p className="text-xs text-muted-foreground">Personal access tokens</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your Tokens</CardTitle>
          <CardDescription>Personal access tokens for API and CLI access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tokens.map((token: PersonalAccessToken) => (
              <div
                key={token.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <KeyIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{token.name}</h3>
                    <p className="text-sm text-gray-500">
                      Created {new Date(token.createdAt).toLocaleDateString()}
                      {token.expiresAt && ` • Expires ${new Date(token.expiresAt).toLocaleDateString()}`}
                      {token.organizationId && ' • Organization-scoped'}
                      {token.allowedOrganizations && token.allowedOrganizations.length > 0 && 
                        ` • ${token.allowedOrganizations.length} organization(s)`}
                      {!token.organizationId && !token.allowedOrganizations && ' • All organizations'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge variant={token.status === 'active' ? 'default' : 'secondary'}>
                    {token.status}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setTokenToDelete({ id: token.id, name: token.name });
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {tokens.length === 0 && (
              <div className="text-center py-12">
                <KeyIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No tokens yet</h3>
                <p className="text-gray-500 mb-4">Create a personal access token to access the API</p>
                <Button onClick={() => setCreateModalOpen(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Token
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <CreateTokenModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={() => {
          // Token list will refresh automatically via query invalidation
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Personal Access Token</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the token "{tokenToDelete?.name}"? This action cannot be undone and any applications using this token will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTokenToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (tokenToDelete) {
                  try {
                    await deleteTokenMutation.mutateAsync({
                      tokenId: tokenToDelete.id,
                    });
                    toast.success('Token deleted successfully');
                    setDeleteDialogOpen(false);
                    setTokenToDelete(null);
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Failed to delete token');
                  }
                }
              }}
              disabled={deleteTokenMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteTokenMutation.isPending ? 'Deleting...' : 'Delete Token'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
